import { Box, FormControl, FormLabel, Select, Text } from '@chakra-ui/react'
import { Language, Person, Star } from '@mui/icons-material'
import {
  Avatar,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from '@mui/material'
import { ChangeEvent, FC, useMemo } from 'react'
import { Check } from 'react-feather'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { useLocale } from '../../../hooks/useLocale'
import { PeerAssessmentItem } from '../../../types/api'
import {
  PeerAssessmentHeader,
  PeerAssessmentUsersList,
} from './peer-assessment.style'
import { DEFAULT_LOCALE } from '~/app/constants'
import {
  getPeerAssessmentLeadershipValuesLink,
  getPeerAssessmentLink,
} from '../../../utils/assessment'

interface UserInfoDisplayProps {
  name: string
  email: string
  type?: string
}

const UserInfoDisplay: FC<UserInfoDisplayProps> = ({ name, email, type }) => {
  const { t } = useLocale()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          font: '400 14px Poppins, sans-serif',
          color: 'rgba(0, 0, 0, 0.87)',
        }}
      >
        {name}
      </div>
      <div
        style={{
          font: '400 12px Poppins, sans-serif',
          color: 'rgba(0, 0, 0, 0.6)',
        }}
      >
        {email}
      </div>
      {type && (
        <div
          style={{
            font: '400 11px Poppins, sans-serif',
            color: 'rgba(0, 0, 0, 0.38)',
            marginTop: '2px',
          }}
        >
          {type === 'peer-assessment'
            ? t('start_of_journey')
            : t('end_of_journey')}
        </div>
      )}
    </div>
  )
}

export function PeerAssessmentUsers({
  requests,
  company,
  isMobileMenuOpen,
  language,
  handleChangeLanguage,
}: {
  requests: PeerAssessmentItem[]
  company?: string
  isMobileMenuOpen: boolean
  language: string
  handleChangeLanguage: (value: any) => void
}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isEndJourney = useMemo(
    () => searchParams.get('type') === 'endjourney',
    [searchParams]
  )

  const { user, author } = useParams<{
    user: string
    author: string
    question: string
  }>()
  const { t } = useLocale()
  const { pathname } = useLocation()

  const pendingRequests = useMemo(() => {
    return requests.filter((request) => request.status !== 'COMPLETED')
  }, [requests])

  const completedRequests = useMemo(() => {
    return requests.filter((request) => request.status === 'COMPLETED')
  }, [requests])

  return (
    <PeerAssessmentUsersList className={isMobileMenuOpen ? 'open' : ''}>
      <PeerAssessmentHeader>
        <img src="/images/hupo-logo.svg" alt="Hupo" />
      </PeerAssessmentHeader>
      <Box>
        <Text fontSize="sm" fontWeight="bold" color="gray.500" mt={4} mb={2}>
          {t('leadership_values')}
        </Text>
        <ListItemButton
          selected={pathname.includes('u/q/leadership-values')}
          disableRipple
          onClick={() =>
            navigate(getPeerAssessmentLeadershipValuesLink(author))
          }
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'warning.light' }}>
              <Star />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary={company} />
        </ListItemButton>
      </Box>
      <Box>
        {pendingRequests.length > 0 ? (
          <Text fontSize="sm" fontWeight="bold" color="gray.500" mt={4} mb={2}>
            {t('pending_requests')}
          </Text>
        ) : (
          <></>
        )}
        {pendingRequests.map((question) => (
          <ListItemButton
            disableRipple
            selected={
              user === question.user._id &&
              ((question.type === 'peer-assessment' && !isEndJourney) ||
                (question.type === 'peer-end-of-journey' && isEndJourney))
            }
            key={question.user._id + '_' + question.type}
            onClick={() => {
              const questionId =
                question.questionIndex &&
                question.questionIndex < question.questions.length
                  ? question.questions[question.questionIndex]?._id
                  : question.questions[0]._id

              const peerAssessmentLink = getPeerAssessmentLink({
                author,
                userId: question.user._id,
                questionId,
                isEndJourney: question.type === 'peer-end-of-journey',
              })
              navigate(peerAssessmentLink)
            }}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: 'error.light' }}>
                <Person />
              </Avatar>
            </ListItemAvatar>
            <UserInfoDisplay
              name={question.user.name}
              email={question.user.email}
              type={question.type}
            />
          </ListItemButton>
        ))}
      </Box>

      <Box>
        {completedRequests.length > 0 ? (
          <Text fontSize="sm" fontWeight="bold" color="gray.500" mt={4} mb={2}>
            {t('completed_requests')}
          </Text>
        ) : (
          <></>
        )}
        {completedRequests.map((question) => (
          <ListItemButton
            disableRipple
            selected={
              user === question.user._id &&
              ((question.type === 'peer-assessment' && !isEndJourney) ||
                (question.type === 'peer-end-of-journey' && isEndJourney))
            }
            key={question.user._id + '_' + question.type}
            onClick={() => {
              const peerAssessmentLink = getPeerAssessmentLink({
                author,
                userId: question.user._id,
                questionId:
                  question.questions[question.questions.length - 1]._id,
                isEndJourney: question.type === 'peer-end-of-journey',
              })
              navigate(peerAssessmentLink)
            }}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: 'success.light' }}>
                <Check />
              </Avatar>
            </ListItemAvatar>
            <UserInfoDisplay
              name={question.user.name}
              email={question.user.email}
              type={question.type}
            />
          </ListItemButton>
        ))}
      </Box>
      <Box
        position="absolute"
        bottom={0}
        left={0}
        height="auto"
        width={300}
        backgroundColor="rgba(253, 233, 230, 0.98)"
        p={4}
      >
        <FormControl id="language">
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.500">
            <Language sx={{ marginRight: '4px' }} />
            {t('language')}
          </FormLabel>
          <Select
            name="language"
            value={language}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              handleChangeLanguage(e.target.value ?? DEFAULT_LOCALE)
            }
            variant="outline"
            borderColor="gray.300"
            sx={{ paddingX: 4, paddingY: 1 }}
          >
            <option value="en">English</option>
            <option value="ko">한국어</option>
            <option value="ja">日本語</option>
            <option value="zh">中文（简体）</option>
            <option value="tw">中文（繁體）</option>
            <option value="ru">Русский</option>
          </Select>
        </FormControl>
      </Box>
    </PeerAssessmentUsersList>
  )
}
